// @ts-check
// "Testar bot" no navegador (contrato de bots, seções 7 e 22.2): uma conversa simulada que chama o intérprete puro
// (`stepBotRun`, assinatura da seção 7.1) e faz o papel do executor, sem banco nem WhatsApp: as portas do atendimento,
// da retenção e do CRM respondem pelos seis cenários (`createTestBotPorts`), os SEND viram balões, as integrações
// respondem como simuladas e as esperas viram os botões "Lembrete" e "Tempo esgotado". Mostra o caminho (passos sem
// texto), as variáveis (sensíveis como "informado") e o resumo que a equipe receberia. Nada vai ao servidor.
//
// O intérprete é o `stepBotRun` de `shared/bots/bot-interpreter.js`, o mesmo do Durable Object: o teste no navegador e
// a execução real andam pelo mesmo caminho. `registerBotInterpreter` só troca o intérprete (testes).
import { MAXIMUM_BOT_NODES_PER_EVENT, MAXIMUM_BOT_STEPS_PER_RUN } from '../../../shared/bots/bot-limits.js';
import { createBotRunState, detectGlobalWord, stepBotRun } from '../../../shared/bots/bot-interpreter.js';
import { indexBotTree } from './bot-tree.js';
import { renderBotTemplate } from './bot-variables.js';

/**
 * @typedef {import('../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {'CURRENT_FAMILY' | 'SHARED_PHONE' | 'UNKNOWN_NUMBER' | 'NEW_LEAD' | 'TEAM_CLOSED' | 'NO_AGENTS'} BotTestScenarioKey
 * @typedef {import('../../../shared/bots/bot-interpreter.js').BotEvent} BotRunEvent
 * @typedef {import('../../../shared/bots/bot-interpreter.js').BotEffect} BotEffect
 * @typedef {import('../../../shared/bots/bot-interpreter.js').BotStepLog} BotStepLog
 * @typedef {{ run: Record<string, any>, effects: BotEffect[], steps: BotStepLog[], ended: null | { status: string, reason: string } }} BotInterpreterOutput
 * @typedef {(input: { definition: BotDefinition, run: any, event: BotRunEvent, context: Record<string, any>, now: string,
 *   limits: Record<string, number> }) => BotInterpreterOutput} BotInterpreter
 * @typedef {{ id: string, title: string, description?: string | null }} BotChatOption
 * @typedef {{ id: string, direction: 'INCOMING' | 'OUTGOING' | 'NOTE', text: string, header?: string | null, footer?: string | null, kind: string,
 *   options: BotChatOption[], listButtonLabel?: string | null, noteKey?: string, noteParams?: Record<string, unknown> }} BotChatMessage
 * @typedef {{ definition: BotDefinition, botName: string, scenario: BotTestScenarioKey, ports: ReturnType<typeof createTestBotPorts>,
 *   context: Record<string, any>, run: Record<string, any> | null, messages: BotChatMessage[], steps: BotStepLog[], ended: null | { status: string, reason: string },
 *   waiting: null | { resumeKind: string, resumeAt: string | null }, error: string | null, sequence: number }} BotTestState
 */

export const BOT_TEST_SCENARIOS = Object.freeze(/** @type {BotTestScenarioKey[]} */ (['CURRENT_FAMILY', 'SHARED_PHONE', 'UNKNOWN_NUMBER', 'NEW_LEAD', 'TEAM_CLOSED', 'NO_AGENTS']));

/** CPF e data de nascimento que o cenário "Número desconhecido" aceita (mostrados como dica no painel). */
export const BOT_TEST_WEAK_DATA = Object.freeze({ cpf: '529.982.247-25', birthDate: '12/03/2016' });
/** Código que a verificação forte aceita no teste. */
export const BOT_TEST_STRONG_CODE = '123456';

const GUARDIAN = Object.freeze({ personId: 'test-guardian-ana', firstName: 'Ana', name: 'Ana Souza' });
const OTHER_GUARDIAN = Object.freeze({ personId: 'test-guardian-carlos', firstName: 'Carlos', name: 'Carlos Souza' });
const STUDENTS = Object.freeze([
  Object.freeze({ personId: 'test-student-lucas', firstName: 'Lucas', name: 'Lucas Souza', gradeLevelLabel: '5º ano', disclosure: { GENERAL: 'ALLOWED', ADMINISTRATIVE: 'ALLOWED', PEDAGOGICAL: 'ALLOWED', FINANCIAL: 'ALLOWED' } }),
  Object.freeze({ personId: 'test-student-marina', firstName: 'Marina', name: 'Marina Souza', gradeLevelLabel: '2º ano', disclosure: { GENERAL: 'ALLOWED', ADMINISTRATIVE: 'ALLOWED', PEDAGOGICAL: 'ALLOWED', FINANCIAL: 'ALLOWED' } }),
]);
const DYNAMIC_OPTIONS = Object.freeze({
  CAMPUSES: [{ id: 'test-campus-1', title: 'Unidade Centro' }, { id: 'test-campus-2', title: 'Unidade Sul' }],
  SEGMENTS: [{ id: 'test-segment-1', title: 'Educação Infantil' }, { id: 'test-segment-2', title: 'Fundamental I' }, { id: 'test-segment-3', title: 'Fundamental II' }, { id: 'test-segment-4', title: 'Ensino Médio' }],
  COURSES: [{ id: 'test-course-1', title: '1º ano' }, { id: 'test-course-2', title: '2º ano' }, { id: 'test-course-3', title: '3º ano' }, { id: 'test-course-4', title: '4º ano' }, { id: 'test-course-5', title: '5º ano' }],
  ACADEMIC_PERIODS: [{ id: 'test-period-1', title: '2027' }, { id: 'test-period-2', title: '2028' }],
});

/** @type {BotInterpreter} */
const REAL_INTERPRETER = (input) => stepBotRun(/** @type {any} */ (input));

/** @type {BotInterpreter | null} */
let registeredInterpreter = REAL_INTERPRETER;

/** Troca o intérprete do "Testar bot" (testes); nulo volta ao real. @param {BotInterpreter | null} interpreter */
export function registerBotInterpreter(interpreter) {
  registeredInterpreter = interpreter ?? REAL_INTERPRETER;
}

/** O intérprete ligado, ou nulo (o painel avisa que o teste ainda não está disponível). */
export const botInterpreter = () => registeredInterpreter;

/**
 * Horários de exemplo da página de agendamento: dias úteis a partir de amanhã, às 09:00, 10:30 e 14:00 (horário de
 * Brasília), do instante pedido em diante.
 * @param {string | null | undefined} from @param {Date} now @param {number} limit
 */
function testBookableSlots(from, now, limit) {
  const start = Math.max(Date.parse(String(from ?? '')) || 0, now.getTime());
  /** @type {Array<{ startsAt: string, endsAt: string, hostWorkspaceMemberId: null }>} */
  const slots = [];
  const day = new Date(now.getTime());
  day.setUTCHours(0, 0, 0, 0);
  for (let offset = 1; offset <= 30 && slots.length < limit; offset += 1) {
    const date = new Date(day.getTime() + offset * 86_400_000);
    if (date.getUTCDay() === 0 || date.getUTCDay() === 6) continue;
    for (const [hour, minute] of [[12, 0], [13, 30], [17, 0]]) {
      const startsAt = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hour, minute);
      if (startsAt < start || slots.length >= limit) continue;
      slots.push({ startsAt: new Date(startsAt).toISOString(), endsAt: new Date(startsAt + 3_600_000).toISOString(), hostWorkspaceMemberId: null });
    }
  }
  return slots;
}

/** @param {Date} now */
function nextOpeningOf(now) {
  const tomorrow = new Date(now.getTime() + 86_400_000);
  tomorrow.setUTCHours(11, 0, 0, 0);
  return tomorrow.toISOString();
}

/**
 * Portas falsas do cenário, com as assinaturas das portas reais (contrato de atendimento, seção 22.4; contrato de
 * bots, seção 7.4). O runtime é ignorado.
 * @param {BotTestScenarioKey} scenario @param {{ now?: Date }} [options]
 */
export function createTestBotPorts(scenario, options = {}) {
  const now = options.now ?? new Date();
  const isIdentified = scenario === 'CURRENT_FAMILY' || scenario === 'TEAM_CLOSED' || scenario === 'NO_AGENTS';
  const isTeamOpen = scenario !== 'TEAM_CLOSED';
  const availableAgents = scenario === 'NO_AGENTS' ? 0 : 2;
  /** @type {string | null} */
  let speakerId = isIdentified ? GUARDIAN.personId : null;
  let ticketSequence = 120;
  const protocol = () => `2026-000${(ticketSequence += 1)}`;
  return {
    serviceDesk: {
      identifyByPhone: () => {
        if (isIdentified) return { status: 'IDENTIFIED', personId: GUARDIAN.personId, candidates: [{ personId: GUARDIAN.personId, firstName: GUARDIAN.firstName }] };
        if (scenario === 'SHARED_PHONE') {
          return { status: 'SHARED', personId: null, candidates: [GUARDIAN, OTHER_GUARDIAN].map((person) => ({ personId: person.personId, firstName: person.firstName })) };
        }
        return { status: 'NOT_FOUND', personId: null, candidates: [] };
      },
      confirmSpeaker: (/** @type {any} */ _runtime, /** @type {{ personId: string }} */ input) => {
        speakerId = input.personId;
        return { status: 'IDENTIFIED' };
      },
      identifyByWeakData: (/** @type {any} */ _runtime, /** @type {{ cpf: string, studentBirthDate: string }} */ input) => {
        const digits = String(input.cpf ?? '').replace(/\D/g, '');
        if (digits === BOT_TEST_WEAK_DATA.cpf.replace(/\D/g, '') && String(input.studentBirthDate ?? '').startsWith('2016-03-12')) {
          speakerId = GUARDIAN.personId;
          return { status: 'IDENTIFIED', personId: GUARDIAN.personId };
        }
        return { status: 'NOT_FOUND', personId: null };
      },
      listStudentsForSpeaker: () => (speakerId ? STUDENTS.map((student) => ({ ...student, disclosure: { ...student.disclosure } })) : []),
      chooseStudents: () => undefined,
      requestStrongCode: () => (speakerId ? { status: 'SENT', codeId: 'test-code', destinationMask: 'a***@email.com' } : { status: 'UNAVAILABLE', codeId: null, destinationMask: null }),
      verifyStrongCode: (/** @type {any} */ _runtime, /** @type {{ code: string }} */ input) => (String(input.code ?? '').replace(/\D/g, '') === BOT_TEST_STRONG_CODE
        ? { status: 'VERIFIED', attemptsLeft: 5 } : { status: 'INVALID', attemptsLeft: 4 }),
      openTicket: () => ({ ticketId: 'test-ticket', protocol: protocol(), created: true }),
      handoffThreadToTeam: () => ({ ticketId: 'test-ticket', protocol: protocol(), isTeamOpen, nextOpeningAt: isTeamOpen ? null : nextOpeningOf(now), availableAgents }),
      isTeamOpenNow: () => ({ isOpen: isTeamOpen, nextOpeningAt: isTeamOpen ? null : nextOpeningOf(now) }),
      describeThreadForBot: () => ({
        purpose: scenario === 'NEW_LEAD' || scenario === 'UNKNOWN_NUMBER' ? 'PENDING' : 'SERVICE', familyStatus: isIdentified ? 'CURRENT' : 'UNKNOWN',
        speakerPersonId: speakerId, identityLevel: isIdentified ? 'PHONE_MATCH' : 'UNKNOWN', activeTicketId: null, queueTeamId: null,
      }),
      settlePendingPurpose: () => undefined,
    },
    retention: {
      openRetentionCase: () => ({ ticketId: 'test-retention', protocol: protocol(), created: true }),
    },
    crm: {
      createCaptureCard: () => ({ cardId: 'test-card', created: true }),
      listDynamicOptions: (/** @type {any} */ _runtime, /** @type {{ source: keyof typeof DYNAMIC_OPTIONS }} */ input) => ({
        options: (DYNAMIC_OPTIONS[input?.source] ?? []).map((option) => ({ ...option })),
      }),
      executeCrmAction: () => ({ status: 'PASSED' }),
      writeAnswerField: () => ({ status: 'PASSED' }),
    },
    // a página de agendamento (agendamento integrado): horários de exemplo e reserva sempre confirmada
    booking: {
      listBookableSlots: (/** @type {any} */ _runtime, /** @type {{ from?: string | null, limit?: number }} */ input) => ({
        timeZone: 'America/Sao_Paulo', slots: testBookableSlots(input?.from ?? null, now, Number(input?.limit ?? 10)),
      }),
      bookSlot: (/** @type {any} */ _runtime, /** @type {{ startsAt: string }} */ input) => ({
        status: 'CONFIRMED', bookingId: 'test-booking', calendarEventId: null, startsAt: input?.startsAt ?? null,
        endsAt: input?.startsAt ? new Date(Date.parse(input.startsAt) + 3_600_000).toISOString() : null,
      }),
    },
  };
}

/**
 * Contexto que o executor montaria para o cenário (seção 7.1).
 * @param {BotTestScenarioKey} scenario
 * @param {{ institutionName: string, channelId?: string | null, teamIds?: string[], teamNames?: Record<string, string>, botsById?: Record<string, any>, now?: Date,
 *   contactName?: string, botName?: string | null }} options
 *   teamNames: nome de cada departamento (o texto da transferência no teste)
 */
export function buildTestBotContext(scenario, options) {
  const now = options.now ?? new Date();
  const isIdentified = scenario === 'CURRENT_FAMILY' || scenario === 'TEAM_CLOSED' || scenario === 'NO_AGENTS';
  const isTeamOpen = scenario !== 'TEAM_CLOSED';
  const contactName = options.contactName ?? (isIdentified || scenario === 'SHARED_PHONE' ? GUARDIAN.name : 'Patrícia Moreira');
  /** @type {Record<string, { isOpen: boolean, nextOpeningAt: string | null }>} */
  const teamOpen = {};
  for (const teamId of options.teamIds ?? []) teamOpen[teamId] = { isOpen: isTeamOpen, nextOpeningAt: isTeamOpen ? null : nextOpeningOf(now) };
  return {
    threadId: 'test-thread',
    channelId: options.channelId ?? null,
    botName: options.botName ?? null,
    teamsById: Object.fromEntries(Object.entries(options.teamNames ?? {}).map(([teamId, name]) => [teamId, { name }])),
    threadPurpose: scenario === 'NEW_LEAD' || scenario === 'UNKNOWN_NUMBER' || scenario === 'SHARED_PHONE' ? 'PENDING' : 'SERVICE',
    familyStatus: isIdentified ? 'CURRENT' : scenario === 'NEW_LEAD' ? 'UNKNOWN' : 'UNKNOWN',
    identityLevel: isIdentified ? 'PHONE_MATCH' : 'UNKNOWN',
    teamOpen,
    institutionName: options.institutionName,
    contact: { firstName: contactName.split(' ')[0], name: contactName },
    guardian: isIdentified ? { ...GUARDIAN } : null,
    students: isIdentified ? STUDENTS.map((student) => ({ ...student })) : [],
    ticket: null,
    card: null,
    botsById: options.botsById ?? {},
    teamNames: options.teamNames ?? {},
  };
}

/** Valores dos limites que o executor passa ao intérprete. */
export const BOT_TEST_LIMITS = Object.freeze({ nodesPerEvent: MAXIMUM_BOT_NODES_PER_EVENT, stepsPerRun: MAXIMUM_BOT_STEPS_PER_RUN });

/**
 * A mensagem inteira é uma palavra global ("menu", "atendente")? O executor marca antes de chamar o intérprete, pela
 * mesma regra do motor (`detectGlobalWord`).
 * @param {BotDefinition} definition @param {string} text
 */
export function isGlobalWord(definition, text) {
  return detectGlobalWord(definition.settings, text) !== null;
}

/**
 * Balão da conversa a partir da mensagem de um SEND: o formato do intérprete (`kind`, `text`, `options`) ou o pedido da
 * Cloud API (`type: 'interactive'`), para a troca pelo intérprete real não mudar a tela.
 * @param {Record<string, any>} message @param {string} id
 * @returns {BotChatMessage}
 */
export function chatMessageOf(message, id) {
  const interactive = message?.interactive;
  if (interactive) {
    const isList = interactive.type === 'list';
    const options = isList
      ? (interactive.action?.sections ?? []).flatMap((/** @type {any} */ section) => section.rows ?? []).map((/** @type {any} */ row) => ({ id: row.id, title: row.title, description: row.description ?? null }))
      : (interactive.action?.buttons ?? []).map((/** @type {any} */ button) => ({ id: button.reply?.id, title: button.reply?.title }));
    return {
      id, direction: 'OUTGOING', kind: isList ? 'LIST' : 'BUTTONS', text: interactive.body?.text ?? '', header: interactive.header?.text ?? null,
      footer: interactive.footer?.text ?? null, options, listButtonLabel: isList ? interactive.action?.button ?? null : null,
    };
  }
  // a mensagem do intérprete (BotOutgoingMessage): texto, botões, lista ou mídia
  if (message?.kind === 'BUTTONS') {
    return {
      id, direction: 'OUTGOING', kind: 'BUTTONS', text: String(message.body ?? ''), header: message.header ?? null, footer: message.footer ?? null,
      options: (message.buttons ?? []).map((/** @type {any} */ button) => ({ id: String(button.id), title: String(button.title), description: null })), listButtonLabel: null,
    };
  }
  if (message?.kind === 'LIST') {
    return {
      id, direction: 'OUTGOING', kind: 'LIST', text: String(message.body ?? ''), header: message.header ?? null, footer: message.footer ?? null,
      options: (message.sections ?? []).flatMap((/** @type {any} */ section) => section.rows ?? [])
        .map((/** @type {any} */ row) => ({ id: String(row.id), title: String(row.title), description: row.description ?? null })),
      listButtonLabel: message.button ?? null,
    };
  }
  if (message?.kind === 'TEXT' || message?.kind === 'MEDIA') {
    return {
      id, direction: 'OUTGOING', kind: message.kind, text: String(message.kind === 'TEXT' ? message.body ?? '' : message.caption ?? ''), header: null, footer: null,
      options: [], listButtonLabel: null,
    };
  }
  const options = Array.isArray(message?.options) ? message.options.map((/** @type {any} */ option) => ({ id: String(option.id), title: String(option.title), description: option.description ?? null })) : [];
  return {
    id, direction: 'OUTGOING', kind: message?.kind ?? (options.length > 0 ? 'BUTTONS' : 'TEXT'),
    text: typeof message?.text === 'string' ? message.text : String(message?.text?.body ?? message?.body ?? message?.displayText ?? ''), header: message?.header ?? null, footer: message?.footer ?? null,
    options, listButtonLabel: message?.listButtonLabel ?? null,
  };
}

/**
 * Aplica a saída do intérprete como o executor faria e resolve as chamadas de porta e as integrações em seguida.
 * @param {BotTestState} state @param {BotInterpreter} interpret @param {BotRunEvent} event @param {Date} now
 * @returns {BotTestState}
 */
function advance(state, interpret, event, now) {
  let next = { ...state, messages: [...state.messages], steps: [...state.steps] };
  /** @type {BotRunEvent | null} */
  let pending = event;
  let rounds = 0;
  while (pending && rounds < MAXIMUM_BOT_NODES_PER_EVENT * 2) {
    rounds += 1;
    /** @type {BotInterpreterOutput} */
    let output;
    try {
      output = interpret({ definition: next.definition, run: next.run, event: pending, context: next.context, now: now.toISOString(), limits: { ...BOT_TEST_LIMITS } });
    } catch (error) {
      return { ...next, error: error instanceof Error ? error.message : String(error) };
    }
    pending = null;
    next.run = output.run;
    next.steps.push(...(output.steps ?? []));
    for (const effect of output.effects ?? []) {
      if (effect.kind === 'SEND') {
        next.sequence += 1;
        next.messages.push(chatMessageOf(effect.message ?? {}, `bot-${next.sequence}`));
      } else if (effect.kind === 'SET_WAIT') {
        next.waiting = effect.resumeKind ? { resumeKind: effect.resumeKind, resumeAt: effect.resumeAt ?? null } : null;
      } else if ((effect.kind === 'PORT_CALL' || effect.kind === 'EXTERNAL') && !pending) {
        pending = { kind: 'EFFECT_RESULT', effectId: effect.effectId, result: resolveEffect(next, effect) };
        if (effect.kind === 'EXTERNAL') {
          next.sequence += 1;
          next.messages.push({ id: `note-${next.sequence}`, direction: 'NOTE', kind: 'NOTE', text: '', options: [], noteKey: 'bots.test.integrationSimulated', noteParams: { kind: effect.externalKind } });
        }
      }
    }
    if (output.ended) {
      next.ended = output.ended;
      next.waiting = null;
      pending = null;
    }
  }
  return next;
}

/** @param {BotTestState} state @param {BotEffect} effect */
function resolveEffect(state, effect) {
  if (effect.kind === 'EXTERNAL') return { ok: true, status: 200, body: { simulated: true } };
  if (effect.kind !== 'PORT_CALL') return { error: { code: 'PORT_METHOD_MISSING' } };
  const port = /** @type {Record<string, any>} */ (state.ports)[effect.port];
  const method = port?.[effect.method];
  if (typeof method !== 'function') return { error: { code: 'PORT_METHOD_MISSING' } };
  try {
    return method(null, effect.input ?? {}) ?? null;
  } catch (error) {
    return { error: { code: 'ERROR', message: error instanceof Error ? error.message : String(error) } };
  }
}

/**
 * Começa o teste: o gatilho do cenário e a primeira rodada do bot.
 * @param {{ definition: BotDefinition, botId: string, botName: string, scenario: BotTestScenarioKey, interpret: BotInterpreter,
 *   institutionName: string, teamIds?: string[], teamNames?: Record<string, string>, botsById?: Record<string, any>, firstMessage?: string, now?: Date }} input
 * @returns {BotTestState}
 */
export function startBotTest(input) {
  const now = input.now ?? new Date();
  const ports = createTestBotPorts(input.scenario, { now });
  const firstTrigger = /** @type {import('../../../shared/bots/bot-definition.js').BotTrigger} */ (input.definition.triggers?.[0] ?? { id: 'manual', kind: 'MANUAL' });
  const channelId = firstTrigger.channelIds?.[0] ?? null;
  /** @type {BotTestState} */
  const state = {
    definition: input.definition, botName: input.botName, scenario: input.scenario, ports,
    context: buildTestBotContext(input.scenario, {
      institutionName: input.institutionName, channelId, teamIds: input.teamIds, teamNames: input.teamNames, botsById: input.botsById, now, botName: input.botName,
    }),
    run: createBotRunState({ id: `test-run-${input.botId}`, botId: input.botId, revision: 0 }),
    messages: [], steps: [], ended: null, waiting: null, error: null, sequence: 0,
  };
  const text = input.firstMessage ?? '';
  if (text) {
    state.sequence += 1;
    state.messages.push({ id: `family-${state.sequence}`, direction: 'INCOMING', kind: 'TEXT', text, options: [] });
  }
  const keyword = firstTrigger.kind === 'KEYWORD' ? (firstTrigger.keywords ?? [])[0] ?? null : null;
  return advance(state, input.interpret, { kind: 'START', trigger: { kind: firstTrigger.kind, id: firstTrigger.id ?? null, text, keyword } }, now);
}

/**
 * Resposta da família (texto ou toque num botão ou linha da lista).
 * @param {BotTestState} state @param {BotInterpreter} interpret @param {{ text: string, interactiveId?: string | null, attachments?: any[] }} answer @param {Date} [now]
 */
export function answerBotTest(state, interpret, answer, now = new Date()) {
  if (state.ended) return state;
  const next = { ...state, messages: [...state.messages], sequence: state.sequence + 1, waiting: null };
  next.messages.push({ id: `family-${next.sequence}`, direction: 'INCOMING', kind: 'TEXT', text: answer.text, options: [] });
  const globalWord = answer.interactiveId ? null : detectGlobalWord(state.definition.settings, answer.text);
  return advance(next, interpret, {
    kind: 'MESSAGE', text: answer.text, interactiveId: answer.interactiveId ?? null, attachments: answer.attachments ?? [], isGlobalWord: globalWord, isTextMessage: true,
  }, now);
}

/** O lembrete ou o tempo limite chegaram. @param {BotTestState} state @param {BotInterpreter} interpret @param {'REMINDER' | 'TIMEOUT'} kind @param {Date} [now] */
export function resumeBotTest(state, interpret, kind, now = new Date()) {
  if (state.ended) return state;
  return advance({ ...state, waiting: null }, interpret, { kind }, now);
}

/** Opções do último balão do bot ainda respondível (os botões tocáveis). @param {BotTestState} state */
export function lastBotOptions(state) {
  if (state.ended) return [];
  for (let position = state.messages.length - 1; position >= 0; position -= 1) {
    const message = state.messages[position];
    if (message.direction === 'INCOMING') return [];
    if (message.direction === 'OUTGOING' && message.options.length > 0) return message.options;
  }
  return [];
}

/**
 * Nomes das variáveis do bot que são sensíveis (perguntas de CPF e data, ou marcadas): aparecem como "informado".
 * @param {BotDefinition} definition
 */
export function sensitiveBotVariables(definition) {
  const names = new Set(/** @type {string[]} */ ([]));
  for (const { node } of indexBotTree(definition).values()) {
    if (node.type !== 'ASK') continue;
    const variable = node.config?.saveTo?.variable;
    const isSensitive = node.config?.isSensitive ?? (node.config?.inputKind === 'CPF' || node.config?.inputKind === 'DATE');
    if (variable && isSensitive) names.add(variable);
  }
  return names;
}

/**
 * Variáveis para o painel: nome e valor, com as sensíveis como "informado".
 * @param {BotTestState} state @param {string} informedText
 * @returns {Array<{ name: string, value: string }>}
 */
export function testVariables(state, informedText) {
  // as marcadas pelo intérprete na execução também (a resposta de uma pergunta sensível)
  const sensitive = new Set([...sensitiveBotVariables(state.definition), ...(state.run?.sensitiveVariables ?? [])]);
  return Object.entries(state.run?.variables ?? {}).map(([name, value]) => ({
    name, value: sensitive.has(name) ? informedText : typeof value === 'string' ? value : JSON.stringify(value),
  }));
}

/**
 * Resumo que a equipe receberia na transferência (seção 13.3): "Bot <nome>: <título do bloco>: <resposta>" por pergunta
 * respondida, na ordem, com as sensíveis como "informado".
 * @param {BotTestState} state @param {{ titleOf: (node: any) => string, informedText: string }} options
 * @returns {string[]}
 */
export function testHandoffSummary(state, options) {
  const index = indexBotTree(state.definition);
  const sensitive = new Set([...sensitiveBotVariables(state.definition), ...(state.run?.sensitiveVariables ?? [])]);
  const variables = state.run?.variables ?? {};
  /** @type {string[]} */
  const lines = [];
  const seen = new Set(/** @type {string[]} */ ([]));
  for (const step of state.steps) {
    if (step.status !== 'PASSED' || seen.has(step.nodeId)) continue;
    const node = index.get(step.nodeId)?.node;
    const variable = node?.type === 'ASK' ? node.config?.saveTo?.variable : node?.type === 'SEND_MESSAGE' ? node.config?.options?.saveTo?.variable : null;
    if (!node || !variable || !(variable in variables)) continue;
    seen.add(step.nodeId);
    const value = sensitive.has(variable) ? options.informedText : String(variables[variable]);
    lines.push(`${state.botName}: ${options.titleOf(node)}: ${value}`);
  }
  return lines;
}

/**
 * Escopo de exemplo da prévia no editor: as variáveis do sistema com valores do cenário e as do bot como o nome.
 * @param {{ institutionName: string }} options
 */
export function previewScope(options) {
  return {
    values: {
      contact: { firstName: 'Ana', name: 'Ana Souza' },
      guardian: { firstName: 'Ana', name: 'Ana Souza' },
      student: { firstName: 'Lucas', name: 'Lucas Souza', gradeLevel: '5º ano', classOffering: '5º ano A' },
      ticket: { protocol: '2026-000123', subject: 'Segunda via do boleto' },
      team: { name: 'Secretaria', nextOpeningAt: 'amanhã às 08:00' },
      institution: { name: options.institutionName },
      card: { stageLabel: 'Visita agendada' },
      visit: { dateTime: 'terça-feira, 10:00' },
      verification: { destination: 'a***@email.com' },
      trigger: { keyword: 'boleto', buttonText: 'Quero rematricular' },
    },
    keepUnknown: true,
  };
}

/** Texto com os valores de exemplo (a prévia do editor). @param {string | null | undefined} text @param {{ institutionName: string }} options */
export const renderPreviewText = (text, options) => renderBotTemplate(text, previewScope(options));
